import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const UNIT_LABELS = { eagle: "Eagle", birdie: "Birdie", par: "Par", bogey: "Bogey", double_bogey: "Doble Bogey", other: "Otro" };

/**
 * Generate PDF report for a completed round.
 * Extracted from RoundResults to reduce component complexity.
 */
export function generateRoundPDF({ round, basePlayers, HOLES, PAR_3_HOLES, outPar, inPar, coursePar, totalBasePoints, totalSecondaryPoints, v1BasePoints, v1SecPoints, v2BasePoints, v2SecPoints, tie, baseWins, overallResult, totalUnits, pointValue }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
  const green = [27, 77, 62];
  const gold = [212, 163, 115];
  const dark = [15, 23, 42];
  const gray = [100, 116, 139];
  let y = 15;

  const addTitle = (text, fontSize = 12) => {
    doc.setFontSize(fontSize);
    doc.setTextColor(...green);
    doc.setFont(undefined, "bold");
    doc.text(text, 14, y);
    y += fontSize * 0.5 + 2;
  };

  const addSubtitle = (text) => {
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    doc.setFont(undefined, "normal");
    doc.text(text, 14, y);
    y += 5;
  };

  const checkPage = (need = 30) => {
    if (y + need > doc.internal.pageSize.getHeight() - 10) {
      doc.addPage();
      y = 15;
    }
  };

  // ---- HEADER ----
  doc.setFontSize(20);
  doc.setTextColor(...green);
  doc.setFont(undefined, "bold");
  doc.text("cadii", 14, y);
  doc.setFontSize(10);
  doc.setTextColor(...gray);
  doc.setFont(undefined, "normal");
  doc.text(`${round.name}  |  ${round.date}  |  ${round.course}`, 40, y);
  y += 10;

  // ---- RESULTADO GENERAL ----
  const baseNames = basePlayers.map(p => p.name).join(" + ");
  addTitle("Resultado General", 13);
  doc.setFontSize(10);
  doc.setTextColor(...dark);
  doc.setFont(undefined, "normal");
  const resultText = tie
    ? "Empate"
    : baseWins
      ? `Base gana por ${overallResult} puntos`
      : `Secundarios ganan por ${Math.abs(overallResult)} puntos`;
  doc.text(`Base (${baseNames}): ${totalBasePoints}   vs   Secundarios: ${totalSecondaryPoints}  -  ${resultText}`, 14, y);
  y += 6;
  doc.setFontSize(9);
  doc.text(`1ra Vuelta: Base ${v1BasePoints} - Sec ${v1SecPoints}   |   2da Vuelta: Base ${v2BasePoints} - Sec ${v2SecPoints}`, 14, y);
  y += 8;

  // ---- 1. SCORECARD ----
  addTitle("1. Scorecard Individual");
  const holeNums1to9 = HOLES.slice(0, 9).map(h => String(h.hole));
  const holeNums10to18 = HOLES.slice(9).map(h => String(h.hole));
  const scHead = [["Jugador", ...holeNums1to9, "OUT", ...holeNums10to18, "IN", "TOT"]];
  const scRows = [];

  scRows.push([
    "Par",
    ...HOLES.slice(0, 9).map(h => String(h.par)),
    String(outPar),
    ...HOLES.slice(9).map(h => String(h.par)),
    String(inPar),
    String(coursePar),
  ]);

  round.players.forEach(player => {
    const row = [player.name];
    let outS = 0, inS = 0;
    for (let i = 1; i <= 9; i++) {
      const s = player.scores?.find(sc => sc.hole_number === i);
      row.push(s ? String(s.gross_score) : "-");
      if (s) outS += s.gross_score;
    }
    row.push(String(outS || "-"));
    for (let i = 10; i <= 18; i++) {
      const s = player.scores?.find(sc => sc.hole_number === i);
      row.push(s ? String(s.gross_score) : "-");
      if (s) inS += s.gross_score;
    }
    row.push(String(inS || "-"));
    row.push(String((outS + inS) || "-"));
    scRows.push(row);
  });

  autoTable(doc, {
    head: scHead, body: scRows, startY: y, margin: { left: 14 },
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 1.5, halign: "center" },
    headStyles: { fillColor: green, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: { 0: { halign: "left", cellWidth: 28 } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // ---- 2. ENFRENTAMIENTOS FOURSOME ----
  checkPage(40);
  addTitle("2. Enfrentamientos Foursome");

  round.matchups?.forEach((matchup, idx) => {
    checkPage(25);
    const secNames = matchup.secondary_pair.map(id => round.players.find(p => p.player_id === id)?.name || "?").join(" + ");
    addSubtitle(`E${idx + 1}: Base vs ${secNames}  |  Ventaja: ${matchup.handicap_advantage} a ${matchup.advantage_to === "base" ? "Base" : matchup.advantage_to === "secondary" ? "Sec." : "Ninguno"}${matchup.is_doubled ? "  |  DOBLE" : ""}  |  V1: ${matchup.vuelta1_points > 0 ? "+" : ""}${matchup.vuelta1_points || 0}  V2: ${matchup.vuelta2_points > 0 ? "+" : ""}${matchup.vuelta2_points || 0}  Total: ${matchup.total_points > 0 ? "+" : ""}${matchup.total_points}`);

    const holes = Object.keys(matchup.hole_results || {});
    if (holes.length > 0) {
      const mHead = [["", ...holes.map(h => `H${h}`)]];
      const mRows = [
        ["B.Baja", ...holes.map(h => { const r = matchup.hole_results[h]; const pts = r.low_ball?.points || 0; return `${r.low_ball?.base}-${r.low_ball?.sec} (${pts > 0 ? "+" + pts : pts < 0 ? String(pts) : "0"})`; })],
        ["B.Alta", ...holes.map(h => { const r = matchup.hole_results[h]; const pts = r.high_ball?.points || 0; return `${r.high_ball?.base}-${r.high_ball?.sec} (${pts > 0 ? "+" + pts : pts < 0 ? String(pts) : "0"})`; })],
        ["Unidades", ...holes.map(h => { const up = matchup.hole_results[h].unit_points || 0; return up !== 0 ? (up > 0 ? "+" + up : String(up)) : "-"; })],
        ["Oyes", ...holes.map(h => { const op = matchup.hole_results[h].oyes_points || 0; return op !== 0 ? (op > 0 ? "+" + op : String(op)) : "-"; })],
        ["TOTAL", ...holes.map(h => { const tp = matchup.hole_results[h].hole_points; return tp > 0 ? "+" + tp : String(tp); })],
      ];

      autoTable(doc, {
        head: mHead, body: mRows, startY: y, margin: { left: 14 },
        theme: "grid",
        styles: { fontSize: 6.5, cellPadding: 1.2, halign: "center" },
        headStyles: { fillColor: green, textColor: [255, 255, 255], fontStyle: "bold" },
        columnStyles: { 0: { halign: "left", cellWidth: 16, fontStyle: "bold" } },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
  });

  // ---- 3. INDIVIDUALES ----
  checkPage(40);
  addTitle("3. Individuales (Match Play)");

  const indBalances = {};
  round.players?.forEach(p => { indBalances[p.player_id] = { name: p.name, won: 0, lost: 0, balance: 0 }; });
  round.individual_matchups?.forEach(m => {
    if (m.total_points > 0) { indBalances[m.player1_id].won += m.total_points; indBalances[m.player2_id].lost += m.total_points; }
    else if (m.total_points < 0) { indBalances[m.player2_id].won += Math.abs(m.total_points); indBalances[m.player1_id].lost += Math.abs(m.total_points); }
  });
  Object.keys(indBalances).forEach(id => { indBalances[id].balance = indBalances[id].won - indBalances[id].lost; });

  const indHead = [["Jugador", "Ganados", "Perdidos", "Balance"]];
  const indRows = Object.values(indBalances)
    .sort((a, b) => b.balance - a.balance)
    .map(d => [d.name, `+${d.won}`, `-${d.lost}`, `${d.balance > 0 ? "+" : ""}${d.balance}`]);

  autoTable(doc, {
    head: indHead, body: indRows, startY: y, margin: { left: 14 },
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2, halign: "center" },
    headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255] },
    columnStyles: { 0: { halign: "left" } },
  });
  y = doc.lastAutoTable.finalY + 5;

  checkPage(20);
  addSubtitle("Detalle de enfrentamientos individuales:");
  const indDetailHead = [["Enfrentamiento", "Ventaja", "Total"]];
  const indDetailRows = round.individual_matchups?.map(m => [
    `${m.player1_name} vs ${m.player2_name}`,
    `${m.handicap_difference} a ${m.advantage_to === "player1" ? m.player1_name?.split(" ")[0] : m.advantage_to === "player2" ? m.player2_name?.split(" ")[0] : "Ninguno"}`,
    `${m.total_points > 0 ? "+" : ""}${m.total_points}`,
  ]) || [];

  autoTable(doc, {
    head: indDetailHead, body: indDetailRows, startY: y, margin: { left: 14 },
    theme: "grid",
    styles: { fontSize: 7.5, cellPadding: 1.8, halign: "center" },
    headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255] },
    columnStyles: { 0: { halign: "left", cellWidth: 60 } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // ---- 4. OYES ----
  checkPage(30);
  addTitle("4. Oyes (Hoyos Par 3)");
  addSubtitle(`Hoyos par 3: ${PAR_3_HOLES.join(", ")}`);

  const oyesHead = [["Hoyo", "1er Lugar", "2do Lugar"]];
  const oyesRows = PAR_3_HOLES.map(h => {
    const r = round.oyes_results?.[String(h)];
    return [
      `Hoyo ${h}`,
      r?.first_place?.player_name || "Sin datos",
      r?.second_place?.player_name || "Sin datos",
    ];
  });
  autoTable(doc, {
    head: oyesHead, body: oyesRows, startY: y, margin: { left: 14 },
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [217, 119, 6], textColor: [255, 255, 255] },
  });
  y = doc.lastAutoTable.finalY + 5;

  addSubtitle("Puntos oyes por enfrentamiento foursome:");
  const oyesFsHead = [["Enfrentamiento", "Oyes pts"]];
  const oyesFsRows = round.matchups?.map((m, i) => {
    const secN = m.secondary_pair.map(id => round.players.find(p => p.player_id === id)?.name?.split(" ")[0] || "?").join("+");
    return [`E${i + 1}: Base vs ${secN}`, `${(m.total_oyes_points || 0) > 0 ? "+" : ""}${m.total_oyes_points || 0}`];
  }) || [];
  autoTable(doc, {
    head: oyesFsHead, body: oyesFsRows, startY: y, margin: { left: 14 },
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [217, 119, 6], textColor: [255, 255, 255] },
  });
  y = doc.lastAutoTable.finalY + 5;

  // Grand Slam in PDF
  const gsMatchups = round.matchups?.filter(m => m.grandslam?.active) || [];
  if (gsMatchups.length > 0) {
    addSubtitle("Grand Slam:");
    const gsHead = [["Enfrentamiento", "Ganador", "Bonus"]];
    const gsRows = gsMatchups.map((m) => {
      const idx = round.matchups.indexOf(m);
      const secN = m.secondary_pair.map(id => round.players.find(p => p.player_id === id)?.name?.split(" ")[0] || "?").join("+");
      const winner = m.grandslam.team === "base" ? "Base" : secN;
      const bonus = m.grandslam.bonus;
      return [`E${idx + 1}: Base vs ${secN}`, winner, `${bonus > 0 ? "+" : ""}${bonus}`];
    });
    autoTable(doc, {
      head: gsHead, body: gsRows, startY: y, margin: { left: 14 },
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [202, 138, 4], textColor: [255, 255, 255] },
    });
    y = doc.lastAutoTable.finalY + 5;
  }
  y += 5;

  // ---- 5. UNIDADES ----
  checkPage(30);
  addTitle("5. Unidades");
  const uHead = [["Jugador", "Total", "Detalle"]];
  const uRows = round.players
    ?.sort((a, b) => (totalUnits[b.player_id] || 0) - (totalUnits[a.player_id] || 0))
    .map(p => {
      const details = p.scores?.filter(s => s.has_unit && s.unit_count > 0)
        .map(s => `H${s.hole_number}: ${UNIT_LABELS[s.unit_type] || s.unit_type} (${s.unit_count})`)
        .join(", ") || "-";
      return [p.name, String(totalUnits[p.player_id] || 0), details];
    }) || [];
  autoTable(doc, {
    head: uHead, body: uRows, startY: y, margin: { left: 14 },
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: gold, textColor: [255, 255, 255] },
    columnStyles: { 2: { cellWidth: 100 } },
  });
  y = doc.lastAutoTable.finalY + 10;

  // ---- 6. BALANCE ECONOMICO ----
  if (pointValue > 0) {
    checkPage(40);
    addTitle("6. Balance Economico");
    addSubtitle(`Valor por punto: $${pointValue}`);

    const hasGS = round.matchups?.some(m => m.grandslam?.active);
    const gsHeader = hasGS ? ["GS"] : [];
    const bHead = [["Jugador", ...round.players.map(p => p.name.split(" ")[0]), ...round.matchups.map((_, i) => `FS${i + 1}`), ...gsHeader, "Pts", "$"]];
    const bRows = round.players.map(player => {
      const indResults = {};
      round.individual_matchups?.forEach(m => {
        if (m.player1_id === player.player_id) indResults[m.player2_id] = m.total_points;
        else if (m.player2_id === player.player_id) indResults[m.player1_id] = -m.total_points;
      });
      const fsResults = round.matchups.map(m => {
        if (m.base_pair?.includes(player.player_id)) return m.total_points;
        if (m.secondary_pair?.includes(player.player_id)) return -m.total_points;
        return null;
      });
      const gsTotal = (round.matchups || []).reduce((sum, m) => {
        if (!m.grandslam?.active) return sum;
        if (m.base_pair?.includes(player.player_id)) return sum + m.grandslam.bonus;
        if (m.secondary_pair?.includes(player.player_id)) return sum - m.grandslam.bonus;
        return sum;
      }, 0);
      const totalInd = Object.values(indResults).reduce((s, p) => s + p, 0);
      const totalFs = fsResults.reduce((s, p) => s + (p || 0), 0);
      const totalPts = totalInd + totalFs + gsTotal;
      const money = totalPts * pointValue;
      const gsCell = hasGS ? [`${gsTotal > 0 ? "+" : ""}${gsTotal}`] : [];
      return [
        player.name,
        ...round.players.map(opp => opp.player_id === player.player_id ? "-" : `${(indResults[opp.player_id] || 0) > 0 ? "+" : ""}${indResults[opp.player_id] || 0}`),
        ...fsResults.map(p => p === null ? "-" : `${p > 0 ? "+" : ""}${p}`),
        ...gsCell,
        `${totalPts > 0 ? "+" : ""}${totalPts}`,
        `$${money >= 0 ? "+" : "-"}${Math.abs(money).toFixed(0)}`,
      ];
    });

    autoTable(doc, {
      head: bHead, body: bRows, startY: y, margin: { left: 14 },
      theme: "grid",
      styles: { fontSize: 6.5, cellPadding: 1.5, halign: "center" },
      headStyles: { fillColor: green, textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: { 0: { halign: "left", cellWidth: 25 } },
    });
  }

  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(...gray);
    doc.text(`cadii - ${round.name} - ${round.date}`, 14, doc.internal.pageSize.getHeight() - 5);
    doc.text(`Pagina ${i}/${pageCount}`, doc.internal.pageSize.getWidth() - 30, doc.internal.pageSize.getHeight() - 5);
  }

  return doc;
}
